/**
 * Wrappers around the trait queries that pick the Hasura or custom-resolver
 * operation based on appUseNewTraitsEndpoints, and always return the
 * custom-resolver response shape. Each takes the same variables as the
 * generated hook it replaces, so call sites only change which hook they import.
 *
 * Both generated hooks are instantiated with mutually exclusive `enabled`, so
 * exactly one runs and the other stays idle. The active one is returned
 * wholesale — a disabled react-query query reports neither loading nor data, so
 * merging the two results would misreport both.
 */

import type { UseQueryOptions } from "react-query";

import {
  type PredictiveTraitArtifactsLegacyQuery,
  type PredictiveTraitArtifactsQuery,
  type PredictiveTraitArtifactsQueryVariables,
  type PredictiveTraitsByFeatureDefinitionIdLegacyQuery,
  type PredictiveTraitsByFeatureDefinitionIdQuery,
  type PredictiveTraitsByFeatureDefinitionIdQueryVariables,
  type TraitLegacyQuery,
  type TraitQuery,
  type TraitQueryVariables,
  type TraitsForParentModelLegacyQuery,
  type TraitsForParentModelQuery,
  type TraitsForParentModelQueryVariables,
  type TraitsLegacyQuery,
  type TraitsQuery,
  type TraitsQueryVariables,
  usePredictiveTraitArtifactsLegacyQuery,
  usePredictiveTraitArtifactsQuery,
  usePredictiveTraitsByFeatureDefinitionIdLegacyQuery,
  usePredictiveTraitsByFeatureDefinitionIdQuery,
  useTraitLegacyQuery,
  useTraitQuery,
  useTraitsForParentModelLegacyQuery,
  useTraitsForParentModelQuery,
  useTraitsLegacyQuery,
  useTraitsQuery,
} from "src/graphql";
import { useNewTraitsEndpoints } from "src/hooks/use-new-traits-endpoints";
import {
  asNewShape,
  asNewShapes,
  toLegacyTraitsFilter,
  toLegacyTraitsOrderBy,
} from "./dual-path";

/**
 * The react-query options the trait call sites use. Deliberately narrow: every
 * option here has to make sense for both operations, and `select` has to be
 * re-pointed at the reshaped legacy response, so options are listed rather than
 * forwarded wholesale.
 */
type DualQueryOptions<TQuery, TData, TError> = Pick<
  UseQueryOptions<TQuery, TError, TData>,
  "enabled" | "select" | "suspense"
> & {
  // react-query's own signature also passes the `Query`, which is typed per
  // operation and so can't be shared between the two paths. Callers only use
  // the data.
  refetchInterval?:
    | number
    | false
    | ((data: TData | undefined) => number | false);
};

/**
 * Builds the legacy query's options: the same behavioral options, plus a
 * `select` that reshapes the legacy response before the caller's own `select`
 * sees it. Without a caller `select`, `TData` is the query type itself — which
 * the compiler can't prove for a type parameter, hence the assertion.
 */
const legacyQueryOptions = <TLegacyQuery, TQuery, TData, TError>(
  options: DualQueryOptions<TQuery, TData, TError> | undefined,
  enabled: boolean,
  toNewShape: (data: TLegacyQuery) => TQuery,
): UseQueryOptions<TLegacyQuery, TError, TData> => ({
  enabled,
  suspense: options?.suspense,
  refetchInterval: options?.refetchInterval,
  select: (data) => {
    const reshaped = toNewShape(data);
    return options?.select
      ? options.select(reshaped)
      : (reshaped as unknown as TData);
  },
});

const toNewTraitsShape = (data: TraitsLegacyQuery): TraitsQuery => ({
  listTraitDefinitions: {
    traits: asNewShapes<TraitsQuery["listTraitDefinitions"]["traits"][number]>(
      data.trait_definitions,
    ),
    totalCount: data.trait_definitions_aggregate.aggregate?.count ?? 0,
  },
});

export const useTraits = <TData = TraitsQuery, TError = Error>(
  variables: TraitsQueryVariables,
  options?: DualQueryOptions<TraitsQuery, TData, TError>,
) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const enabled = options?.enabled ?? true;

  const newQuery = useTraitsQuery<TData, TError>(variables, {
    ...options,
    enabled: enabled && shouldUseNewEndpoints,
  });
  const legacyQuery = useTraitsLegacyQuery<TData, TError>(
    {
      filter: toLegacyTraitsFilter(variables),
      orderBy: toLegacyTraitsOrderBy(
        variables.sortKey,
        variables.sortDirection,
      ),
      limit: variables.limit,
      offset: variables.offset,
    },
    legacyQueryOptions(
      options,
      enabled && !shouldUseNewEndpoints,
      toNewTraitsShape,
    ),
  );

  return shouldUseNewEndpoints ? newQuery : legacyQuery;
};

const toNewTraitShape = (data: TraitLegacyQuery): TraitQuery => ({
  trait_definitions_by_pk:
    data.trait_definitions_by_pk == null
      ? null
      : asNewShape<NonNullable<TraitQuery["trait_definitions_by_pk"]>>(
          data.trait_definitions_by_pk,
        ),
});

export const useTrait = <TData = TraitQuery, TError = Error>(
  variables: TraitQueryVariables,
  options?: DualQueryOptions<TraitQuery, TData, TError>,
) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const enabled = options?.enabled ?? true;

  const newQuery = useTraitQuery<TData, TError>(variables, {
    ...options,
    enabled: enabled && shouldUseNewEndpoints,
  });
  const legacyQuery = useTraitLegacyQuery<TData, TError>(
    variables,
    legacyQueryOptions(
      options,
      enabled && !shouldUseNewEndpoints,
      toNewTraitShape,
    ),
  );

  return shouldUseNewEndpoints ? newQuery : legacyQuery;
};

const toNewTraitsForParentModelShape = (
  data: TraitsForParentModelLegacyQuery,
): TraitsForParentModelQuery => ({
  trait_definitions: asNewShapes<
    TraitsForParentModelQuery["trait_definitions"][number]
  >(data.trait_definitions),
});

export const useTraitsForParentModel = <
  TData = TraitsForParentModelQuery,
  TError = Error,
>(
  variables: TraitsForParentModelQueryVariables,
  options?: DualQueryOptions<TraitsForParentModelQuery, TData, TError>,
) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const enabled = options?.enabled ?? true;

  const newQuery = useTraitsForParentModelQuery<TData, TError>(variables, {
    ...options,
    enabled: enabled && shouldUseNewEndpoints,
  });
  const legacyQuery = useTraitsForParentModelLegacyQuery<TData, TError>(
    variables,
    legacyQueryOptions(
      options,
      enabled && !shouldUseNewEndpoints,
      toNewTraitsForParentModelShape,
    ),
  );

  return shouldUseNewEndpoints ? newQuery : legacyQuery;
};

const toNewPredictiveTraitArtifactsShape = (
  data: PredictiveTraitArtifactsLegacyQuery,
): PredictiveTraitArtifactsQuery => ({
  trait_definitions_by_pk:
    data.trait_definitions_by_pk == null
      ? null
      : asNewShape<
          NonNullable<PredictiveTraitArtifactsQuery["trait_definitions_by_pk"]>
        >(data.trait_definitions_by_pk),
});

export const usePredictiveTraitArtifacts = <
  TData = PredictiveTraitArtifactsQuery,
  TError = Error,
>(
  variables: PredictiveTraitArtifactsQueryVariables,
  options?: DualQueryOptions<PredictiveTraitArtifactsQuery, TData, TError>,
) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const enabled = options?.enabled ?? true;

  const newQuery = usePredictiveTraitArtifactsQuery<TData, TError>(variables, {
    ...options,
    enabled: enabled && shouldUseNewEndpoints,
  });
  const legacyQuery = usePredictiveTraitArtifactsLegacyQuery<TData, TError>(
    variables,
    legacyQueryOptions(
      options,
      enabled && !shouldUseNewEndpoints,
      toNewPredictiveTraitArtifactsShape,
    ),
  );

  return shouldUseNewEndpoints ? newQuery : legacyQuery;
};

const toNewPredictiveTraitsByFeatureDefinitionIdShape = (
  data: PredictiveTraitsByFeatureDefinitionIdLegacyQuery,
): PredictiveTraitsByFeatureDefinitionIdQuery => ({
  listTraitDefinitions: {
    traits: asNewShapes<
      PredictiveTraitsByFeatureDefinitionIdQuery["listTraitDefinitions"]["traits"][number]
    >(data.trait_definitions),
  },
});

export const usePredictiveTraitsByFeatureDefinitionId = <
  TData = PredictiveTraitsByFeatureDefinitionIdQuery,
  TError = Error,
>(
  variables: PredictiveTraitsByFeatureDefinitionIdQueryVariables,
  options?: DualQueryOptions<
    PredictiveTraitsByFeatureDefinitionIdQuery,
    TData,
    TError
  >,
) => {
  const shouldUseNewEndpoints = useNewTraitsEndpoints();
  const enabled = options?.enabled ?? true;

  const newQuery = usePredictiveTraitsByFeatureDefinitionIdQuery<TData, TError>(
    variables,
    { ...options, enabled: enabled && shouldUseNewEndpoints },
  );
  const legacyQuery = usePredictiveTraitsByFeatureDefinitionIdLegacyQuery<
    TData,
    TError
  >(
    variables,
    legacyQueryOptions(
      options,
      enabled && !shouldUseNewEndpoints,
      toNewPredictiveTraitsByFeatureDefinitionIdShape,
    ),
  );

  return shouldUseNewEndpoints ? newQuery : legacyQuery;
};
